'use client'

import React, {useState} from 'react'
import {client} from '../../../../utils/api-client'

/**
 * @typedef {{
 * billingInfo: {
 *      firstName: string,
 *      lastName: string,
 *      paymentMethod: {
 *          object: "credit_card" | "bank_account_info",
 *          cardType: string,
 *          expMonth: number,
 *          expYear: number,
 *          lastFour: string,
 *      }
 * } | null | undefined,
 * setRecurlyAccountId: (id: string)=>void,
 * verifyCVC: (cvc: string)=>Promise<{ valid: boolean, message: string }>,
 * clearBillingInfo: ()=>void,
 * isCVCVerified: boolean,
 * setIsCVCVerified: (value: boolean)=>void,
 * tryCardOnFile: boolean,
 * setTryCardOnFile: (value: boolean)=>void,
 * isVerifying: boolean,
 * cvc: string,
 * setCVC: (value: string)=>void
 * isFetching: boolean,
 * setIsFetching: (value: boolean)=>void,
 * }} BillingInfoContextType
 */

const BillingInfoContext = React.createContext(null)

export function BillingInfoProvider({children}) {
  const [isVerifying, setIsVerifying] = useState(false)
  const [tryCardOnFile, setTryCardOnFile] = useState(true)
  const [billingInfo, setBillingInfo] = React.useState()
  const [isFetching, setIsFetching] = useState(false)
  const [recurlyAccountId, setRecurlyAccountId] = React.useState()
  const [isCVCVerified, setIsCVCVerified] = React.useState(false)
  const [cvc, setCVC] = useState('')

  async function loadBillingInfo() {
    setIsFetching(true)
    try {
      const result = await client(`accounts/${recurlyAccountId}/billing-info`, {
        method: 'GET',
      })
      setIsFetching(false)
      if (result?.billingInfo?.paymentMethod?.object !== 'credit_card') {
        return undefined
      }
      return result.billingInfo
    } catch {
      setIsFetching(false)
    }
  }

  async function verifyCVC(cvc) {
    setIsVerifying(true)
    try {
      const result = await client(
        `accounts/${recurlyAccountId}/billing-info/verify-cvc`,
        {
          method: 'POST',
          data: {cvc},
        },
      )
      console.log('verifyCVC:', result)
      setIsCVCVerified(result.valid)
      setIsVerifying(false)
      return result
    } catch (error) {
      console.error(error)
      setIsVerifying(false)

      return {
        valid: false,
        message: error.message,
      }
    }
  }

  function clearBillingInfo() {
    setBillingInfo(null)
  }

  React.useEffect(() => {
    if (recurlyAccountId) {
      loadBillingInfo().then(setBillingInfo)
    }
  }, [recurlyAccountId])

  return (
    <BillingInfoContext.Provider
      value={{
        billingInfo,
        setRecurlyAccountId,
        verifyCVC,
        clearBillingInfo,
        isCVCVerified,
        setIsCVCVerified,
        setTryCardOnFile,
        tryCardOnFile,
        isVerifying,
        cvc,
        setCVC,
        isFetching,
        setIsFetching,
      }}
    >
      {children}
    </BillingInfoContext.Provider>
  )
}

/**
 *
 * @returns {BillingInfoContextType}
 */
export function useBillingInfo() {
  const context = React.useContext(BillingInfoContext)
  if (!context) {
    throw new Error(
      "'useBillingInfo' can't be called outside 'BillingInfoProvider'",
    )
  }
  return context
}
